import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toIsoDay } from '@radevents/shared';

/**
 * Monatskalender im Zeitraum-Menü (28.10.2026, Vorbild Apple Music, Spotify, Posh): der erste Tipp
 * setzt „ab“ und filtert sofort, denn nur ein Startdatum ist die häufigste Zeitraum-Nutzung; der
 * zweite Tipp setzt „bis“. Ein Tipp vor das Startdatum verschiebt es. Ersetzt die beiden
 * Datumsfelder, die auf dem Handy je einen Datumswähler des Systems öffneten.
 */
interface Props {
  von: string;
  bis: string;
  onWechsel: (von: string, bis: string) => void;
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const ZeitraumKalender: React.FC<Props> = ({ von, bis, onWechsel }) => {
  const heute = toIsoDay(new Date());
  const [monat, setMonat] = useState(() => {
    if (von) { const [y, mo] = von.split('-').map(Number); return new Date(y, mo - 1, 1); }
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const jahr = monat.getFullYear();
  const m = monat.getMonth();
  const tage = new Date(jahr, m + 1, 0).getDate();
  const versatz = (new Date(jahr, m, 1).getDay() + 6) % 7; // Montag zuerst
  const zellen: (string | null)[] = [
    ...Array.from({ length: versatz }, () => null),
    ...Array.from({ length: tage }, (_, i) => toIsoDay(new Date(jahr, m, i + 1))),
  ];
  const vorigerGesperrt = jahr === new Date().getFullYear() && m <= new Date().getMonth();

  const tippen = (tag: string) => {
    if (!von || bis) { onWechsel(tag, ''); return; }
    if (tag < von) { onWechsel(tag, ''); return; }
    onWechsel(von, tag);
  };

  return (
    <div className="kalender">
      <div className="kalender-kopf">
        <button type="button" onClick={() => setMonat(new Date(jahr, m - 1, 1))} disabled={vorigerGesperrt} aria-label="Voriger Monat"><ChevronLeft size={18} aria-hidden /></button>
        <strong>{MONATE[m]} {jahr}</strong>
        <button type="button" onClick={() => setMonat(new Date(jahr, m + 1, 1))} aria-label="Nächster Monat"><ChevronRight size={18} aria-hidden /></button>
      </div>
      <div className="kalender-raster" role="grid">
        {TAGE.map(t => <span key={t} className="kalender-wt">{t}</span>)}
        {zellen.map((tag, i) => {
          if (!tag) return <span key={`l${i}`} />;
          const vorbei = tag < heute;
          const ende = tag === von || tag === bis;
          const drin = !!von && !!bis && tag > von && tag < bis;
          return (
            <button key={tag} type="button" disabled={vorbei} onClick={() => tippen(tag)} aria-pressed={ende}
              className={`kalender-tag${ende ? ' kalender-tag--ende' : ''}${drin ? ' kalender-tag--drin' : ''}${tag === heute ? ' kalender-tag--heute' : ''}`}>
              {Number(tag.slice(8))}
            </button>
          );
        })}
      </div>
      <p className="kalender-hinweis">{!von || bis ? 'Tag antippen: ab diesem Tag' : 'Noch einen Tag antippen: bis zu diesem Tag'}</p>
    </div>
  );
};

export default ZeitraumKalender;
